/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import type { RefObject } from 'preact';
import type { ListScrollPosition } from '../List.types';
import type { ViewportInfo } from '../List';

import { useLayoutEffect, useRef } from 'preact/hooks';
import { setScrollPosition } from '../utils/ListScrollUtils';

type UseScrollHandlingProps<K> = {
  rootRef: RefObject<HTMLDivElement>;
  viewportInfo: ViewportInfo;
  listId: string;
  isLayoutRequired: boolean;
  scrollPositionOverride?: ListScrollPosition<K>;
};

/**
 * Hook that manages handling scroll events on the Preact TableList
 */
export function useScrollHandling<K extends string | number>({
  rootRef,
  viewportInfo,
  listId,
  isLayoutRequired,
  scrollPositionOverride
}: UseScrollHandlingProps<K>) {
  const scrollPositionOverrideRef = useRef<ListScrollPosition<K>>();

  // update horizontal and/or vertical scroll position if a new value is provided
  // NO DEPENDENCY ARRAY AS THIS SHOULD RUN ON EVERY RENDER
  useLayoutEffect(() => {
    const rootElement = rootRef.current;
    const scrollerElement = viewportInfo.viewportConfig.scrollerRef.current;
    if (
      scrollPositionOverrideRef.current !== scrollPositionOverride &&
      rootElement != null &&
      scrollerElement != null &&
      !isLayoutRequired
    ) {
      setScrollPosition(rootElement, scrollerElement, viewportInfo, listId, scrollPositionOverride);
      scrollPositionOverrideRef.current = scrollPositionOverride;
    }
  });

  return viewportInfo.isExternalScroller ? {} : {};
}
